import React, { createContext, useContext, useMemo } from "react";

// Legacy
import { useAuth } from "../../../context/AuthProvider";
import environment from "../../../env";

import { LoadFinancialStatement } from "../api/use-cases/LoadFinancialStatement";
import { SaveFinancialStatement } from "../api/use-cases/SaveFinancialStatement";

import { makeRepositories } from "../api/repositories/makeRepositories";
import { LoadOccupationDirectory } from "../api/use-cases/LoadOccupationDirectory";

export interface FinancialStatementProviderProps {
  children?: React.ReactNode;
  overrides?: Partial<FinancialStatementUseCases>;
}

export interface FinancialStatementUseCases {
  loadFinancial: LoadFinancialStatement;
  saveFinancial: SaveFinancialStatement;
  getOccupationTitles: LoadOccupationDirectory;
  getOccupationTypes: LoadOccupationDirectory;
}

const SlimFinancialContext = createContext<FinancialStatementUseCases | null>(
  null,
);

export function SlimFinancialProvider({
  children,
  overrides,
}: Readonly<FinancialStatementProviderProps>) {
  const { accessToken } = useAuth();

  const cfg = {
    apiKey: environment.CUSTOMER_PORTAL_API_KEY,
    baseUrl: environment.CUSTOMER_PORTAL_API,
    accessToken,
    organization: environment.CLIENT_NAME,
  };

  const repositories = makeRepositories(cfg);

  const useCases = useMemo<FinancialStatementUseCases>(() => {
    const loadFinancial = new LoadFinancialStatement(
      repositories.financialStatements,
    );
    const saveFinancial = new SaveFinancialStatement(
      repositories.financialStatements,
    );
    const getOccupations = new LoadOccupationDirectory(
      repositories.occupations,
    );

    return {
      loadFinancial,
      saveFinancial,
      getOccupationTitles: getOccupations,
      getOccupationTypes: getOccupations,
      ...(overrides ?? {}),
    };
  }, [repositories.financialStatements, repositories.occupations, overrides]);

  return (
    <SlimFinancialContext.Provider value={useCases}>
      {children}
    </SlimFinancialContext.Provider>
  );
}

export function useSlimFinancial() {
  const ctx = useContext(SlimFinancialContext);
  if (!ctx) {
    throw new Error(
      "useSlimFinancial must be used within a SlimFinancialProvider",
    );
  }
  return ctx;
}
